import React, { useState, useEffect, useRef } from 'react';
import { X, Send, MessageCircle, Smile, Edit3, Trash2, Check, XCircle, RefreshCw } from 'lucide-react';
import PresenceDot from './PresenceDot';
import EmotePicker from './EmotePicker';
import ReactionPicker from './ReactionPicker';
import Reactions from './Reactions';
import WhisperIcon from './WhisperIcon';
import EmoteInput from './EmoteInput';
import { renderEmotesInText } from '../utils/emoteRenderer';

/**
 * Safely format an API error payload into a human-readable string.
 * Handles FastAPI's varied error shapes:
 *   - { detail: "string message" }
 *   - { detail: [{ loc, msg, type }, ...] }  (validation errors)
 *   - { detail: { msg: "..." } }
 *   - plain string
 *   - empty / null
 */
const formatApiError = (error, fallback = 'Something went wrong') => {
  if (!error) return fallback;
  if (typeof error === 'string') return error;
  const d = error.detail ?? error.message ?? error;
  if (typeof d === 'string') return d;
  if (Array.isArray(d)) {
    const msgs = d.map(item => (item && item.msg) || (typeof item === 'string' ? item : null)).filter(Boolean);
    if (msgs.length > 0) return msgs.join(', ');
  }
  if (d && typeof d === 'object' && d.msg) return d.msg;
  return fallback;
};

const WhisperModal = ({ isOpen, onClose, otherUser, currentUser, globalEmotes, customEmotes }) => {
  const [whispers, setWhispers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [messageInput, setMessageInput] = useState('');
  const [sending, setSending] = useState(false);
  const [showEmotePicker, setShowEmotePicker] = useState(false);
  const [editingWhisperId, setEditingWhisperId] = useState(null);
  const [editingText, setEditingText] = useState('');
  const [showReactionPicker, setShowReactionPicker] = useState(null);
  const messagesEndRef = useRef(null);
  const pollIntervalRef = useRef(null);

  const API = process.env.REACT_APP_BACKEND_URL + '/api';

  // Load whisper history
  const loadWhispers = async () => {
    if (!otherUser?.user_id) return;
    
    try {
      const token = localStorage.getItem('beatseek_token');
      const response = await fetch(`${API}/whispers/${otherUser?.user_id || otherUser?.id}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setWhispers(data.whispers || []);
      }
    } catch (error) {
      console.error('Error loading whispers:', error);
    }
  };

  // Send whisper
  const sendWhisper = async () => {
    if (!messageInput.trim() || sending) return;

    setSending(true);
    try {
      const token = localStorage.getItem('beatseek_token');
      const response = await fetch(`${API}/whispers`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          receiver_id: otherUser?.user_id || otherUser?.id,
          message: messageInput.trim()
        })
      });

      if (response.ok) {
        const newWhisper = await response.json();
        setWhispers(prev => [...prev, newWhisper]);
        setMessageInput('');
        scrollToBottom();
      } else {
        const error = await response.json().catch(() => ({}));
        const msg = formatApiError(error, 'Failed to send whisper');
        alert(msg);
      }
    } catch (error) {
      console.error('Error sending whisper:', error);
      alert('Failed to send whisper. Please try again.');
    } finally {
      setSending(false);
    }
  };

  // Scroll to bottom
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // Initial load
  useEffect(() => {
    if (isOpen && otherUser) {
      setLoading(true);
      loadWhispers().finally(() => setLoading(false));
    }
  }, [isOpen, otherUser]);

  // Poll for new messages — DISABLED per user request (was causing excessive fetches).
  // Users can now use the manual refresh icon in the header instead.
  // useEffect(() => {
  //   if (isOpen && otherUser) {
  //     pollIntervalRef.current = setInterval(() => {
  //       loadWhispers();
  //     }, 3000);
  //     return () => {
  //       if (pollIntervalRef.current) {
  //         clearInterval(pollIntervalRef.current);
  //       }
  //     };
  //   }
  // }, [isOpen, otherUser]);

  // Delete entire conversation (soft-delete for current user only)
  const deleteConversation = async () => {
    const otherId = otherUser?.user_id || otherUser?.id;
    if (!otherId) return;
    const otherName = otherUser?.user_name || otherUser?.name || 'this user';
    if (!window.confirm(`Delete conversation with ${otherName}? This only removes it from your view.`)) return;
    try {
      const token = localStorage.getItem('beatseek_token');
      const response = await fetch(`${API}/whispers/conversation/${otherId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      if (response.ok) {
        setWhispers([]);
        onClose();
      } else {
        alert('Failed to delete conversation');
      }
    } catch (error) {
      console.error('Error deleting conversation:', error);
      alert('Failed to delete conversation');
    }
  };

  // Scroll to bottom when whispers change
  useEffect(() => {
    scrollToBottom();
  }, [whispers]);

  // Handle key press
  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendWhisper();
    }
  };

  // Delete whisper
  const deleteWhisper = async (whisperId) => {
    if (!window.confirm('Delete this message?')) return;

    try {
      const token = localStorage.getItem('beatseek_token');
      const response = await fetch(`${API}/whispers/${whisperId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        setWhispers(prev => prev.filter(w => w.id !== whisperId));
      } else {
        alert('Failed to delete message');
      }
    } catch (error) {
      console.error('Error deleting whisper:', error);
      alert('Failed to delete message');
    }
  };

  // Start editing
  const startEditing = (whisper) => {
    setEditingWhisperId(whisper.id);
    setEditingText(whisper.message);
  };

  // Save edit
  const saveEdit = async () => {
    if (!editingText.trim()) return;

    try {
      const token = localStorage.getItem('beatseek_token');
      const response = await fetch(`${API}/whispers/${editingWhisperId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ message: editingText })
      });

      if (response.ok) {
        setWhispers(prev => prev.map(w => 
          w.id === editingWhisperId ? { ...w, message: editingText } : w
        ));
        setEditingWhisperId(null);
        setEditingText('');
      } else {
        alert('Failed to edit message');
      }
    } catch (error) {
      console.error('Error editing whisper:', error);
      alert('Failed to edit message');
    }
  };

  // Cancel editing
  const cancelEditing = () => {
    setEditingWhisperId(null);
    setEditingText('');
  };

  // Add reaction to whisper
  const addReaction = async (whisperId, emoji) => {
    try {
      const token = localStorage.getItem('beatseek_token');
      
      const response = await fetch(`${API}/whispers/${whisperId}/reactions`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ emoji })
      });

      if (response.ok) {
        const data = await response.json();
        // Update whisper reactions in local state
        setWhispers(prev => prev.map(w => 
          w.id === whisperId ? { ...w, reactions: data.reactions } : w
        ));
      }
    } catch (error) {
      console.error('Error adding reaction:', error);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/90 backdrop-blur-md flex items-center justify-center p-4 z-[9999]">
      <div className="bg-gradient-to-br from-gray-900 via-purple-900 to-gray-900 rounded-2xl border border-purple-500/30 w-full max-w-2xl h-[600px] flex flex-col shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-700/50">
          <div className="flex items-center gap-3">
            <div className="relative">
              <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-blue-500 rounded-full flex items-center justify-center text-white font-semibold overflow-hidden">
                {(otherUser?.user_avatar || otherUser?.avatar) ? (
                  <img src={otherUser.user_avatar || otherUser.avatar} alt={otherUser.user_name || otherUser.name || 'User'} className="w-full h-full object-cover" />
                ) : (
                  (otherUser?.user_name || otherUser?.name)?.charAt(0)?.toUpperCase() || 'U'
                )}
              </div>
              <div className="absolute -bottom-0.5 -right-0.5">
                <PresenceDot online={otherUser?.online || false} size="small" />
              </div>
            </div>
            <div>
              <h3 className="text-white font-semibold">{otherUser?.user_name || otherUser?.name || 'User'}</h3>
              <p className="text-xs text-gray-400 flex items-center gap-1.5">
                <WhisperIcon size={24} className="text-purple-400" />
                <span>Private Whisper</span>
              </p>
            </div>
          </div>
          <div className="flex items-center gap-1">
            <button
              onClick={loadWhispers}
              className="text-gray-400 hover:text-white transition-colors p-2 hover:bg-gray-800/50 rounded-lg"
              title="Refresh messages"
              data-testid="whisper-modal-refresh-btn"
            >
              <RefreshCw size={18} />
            </button>
            <button
              onClick={deleteConversation}
              className="text-gray-400 hover:text-red-400 transition-colors p-2 hover:bg-red-500/10 rounded-lg"
              title="Delete conversation"
              data-testid="whisper-modal-delete-conv-btn"
            >
              <Trash2 size={18} />
            </button>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-white transition-colors p-2 hover:bg-gray-800/50 rounded-lg"
              data-testid="whisper-modal-close-btn"
            >
              <X size={20} />
            </button>
          </div>
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {loading ? (
            <div className="flex items-center justify-center h-full">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-500"></div>
            </div>
          ) : whispers.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-center">
              <WhisperIcon size={96} className="text-purple-500/30 mb-4" />
              <h4 className="text-lg font-semibold text-white mb-2">No messages yet</h4>
              <p className="text-gray-400 text-sm">Start the conversation with a whisper!</p>
            </div>
          ) : (
            whispers.map((whisper) => {
              const isOwnMessage = whisper.sender_id === currentUser?.id;
              const isEditing = editingWhisperId === whisper.id;
              
              // Construct proper avatar URL
              const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
              let avatarUrl = null;
              if (whisper.sender_avatar) {
                avatarUrl = whisper.sender_avatar.startsWith('/uploads/') 
                  ? `${BACKEND_URL}/api${whisper.sender_avatar}`
                  : whisper.sender_avatar.startsWith('/api/') 
                  ? `${BACKEND_URL}${whisper.sender_avatar}`
                  : whisper.sender_avatar;
              }
              
              return (
                <div key={whisper.id} className="group mb-4 text-left">
                  <div className="inline-block text-left max-w-full">
                    <div className="flex items-center space-x-2 mb-1 justify-start">
                      {/* Avatar */}
                      <div className="w-5 h-5 bg-gradient-to-br from-purple-500 to-blue-500 rounded-full flex items-center justify-center text-white text-xs font-semibold flex-shrink-0 overflow-hidden">
                        {avatarUrl ? (
                          <img src={avatarUrl} alt={whisper.sender_name} className="w-full h-full rounded-full object-cover" />
                        ) : (
                          whisper.sender_name?.charAt(0).toUpperCase() || 'U'
                        )}
                      </div>
                      
                      <span className="text-sm font-semibold text-gray-200">{whisper.sender_name}</span>
                      <span className="text-xs text-gray-500">
                        {new Date(whisper.timestamp).toLocaleTimeString()}
                      </span>
                      
                      {/* Reaction, Edit, Delete buttons */}
                      {!isEditing && (
                        <div className="flex space-x-2">
                          {/* Reaction button for all messages */}
                          <div className="relative">
                            <button
                              onClick={() => setShowReactionPicker(showReactionPicker === whisper.id ? null : whisper.id)}
                              className="p-1.5 text-gray-400 hover:text-yellow-400 hover:bg-yellow-500/20 rounded-lg transition-all duration-200"
                              title="Add reaction"
                            >
                              <Smile size={16} />
                            </button>
                            {showReactionPicker === whisper.id && (
                              <ReactionPicker
                                onSelect={(emoji) => addReaction(whisper.id, emoji)}
                                onClose={() => setShowReactionPicker(null)}
                                position="bottom"
                              />
                            )}
                          </div>
                          
                          {/* Edit/Delete buttons - only for own messages */}
                          {isOwnMessage && (
                            <>
                              <button
                                onClick={() => startEditing(whisper)}
                                className="p-1.5 text-gray-400 hover:text-blue-400 hover:bg-blue-500/20 rounded-lg transition-all duration-200"
                                title="Edit message"
                              >
                                <Edit3 size={16} />
                              </button>
                              <button
                                onClick={() => deleteWhisper(whisper.id)}
                                className="p-1.5 text-gray-400 hover:text-red-400 hover:bg-red-500/20 rounded-lg transition-all duration-200"
                                title="Delete message"
                              >
                                <Trash2 size={16} />
                              </button>
                            </>
                          )}
                        </div>
                      )}
                    </div>
                    
                    {isEditing ? (
                      // Edit mode
                      <div className="flex items-center space-x-2">
                        <input
                          type="text"
                          value={editingText}
                          onChange={(e) => setEditingText(e.target.value)}
                          onKeyPress={(e) => {
                            if (e.key === 'Enter') saveEdit();
                            else if (e.key === 'Escape') cancelEditing();
                          }}
                          className="flex-1 bg-gray-600 text-white px-2 py-1 rounded text-sm"
                          autoFocus
                        />
                        <button
                          onClick={saveEdit}
                          className="p-1 text-green-400 hover:text-green-300"
                          title="Save changes"
                        >
                          <Check size={14} />
                        </button>
                        <button
                          onClick={cancelEditing}
                          className="p-1 text-gray-400 hover:text-gray-300"
                          title="Cancel editing"
                        >
                          <XCircle size={14} />
                        </button>
                      </div>
                    ) : (
                      <>
                        {/* Normal message display */}
                        <div className={`inline-block px-3 py-2 rounded-lg text-sm ${
                          isOwnMessage 
                            ? 'bg-gradient-to-r from-purple-600 to-blue-600 text-white' 
                            : 'bg-gray-700/50 text-gray-200 border border-gray-600/50'
                        }`}>
                          <span dangerouslySetInnerHTML={{ __html: renderEmotesInText(whisper.message, globalEmotes, customEmotes) }} />
                        </div>
                        
                        {/* Reactions display */}
                        <Reactions
                          reactions={whisper.reactions || []}
                          onReactionClick={(emoji) => addReaction(whisper.id, emoji)}
                          currentUserId={currentUser?.id}
                        />
                      </>
                    )}
                  </div>
                </div>
              );
            })
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Input */}
        <div className="p-4 border-t border-gray-700/50">
          <div className="flex items-center gap-2">
            <EmoteInput
              value={messageInput}
              onChange={setMessageInput}
              onKeyPress={handleKeyPress}
              placeholder="Type a whisper..."
              globalEmotes={globalEmotes}
              customEmotes={customEmotes}
              disabled={sending}
              className="flex-1 bg-gray-800/50 border border-gray-700/50 rounded-lg px-4 py-2 text-white placeholder-gray-500 focus:ring-2 focus:ring-purple-500"
            />
            
            {/* Emote Picker Button */}
            <button
              onClick={() => setShowEmotePicker(!showEmotePicker)}
              className="text-gray-400 hover:text-purple-400 p-2 hover:bg-gray-700/50 rounded-lg transition-colors"
              title="Add emote"
            >
              <Smile size={20} />
            </button>
            
            <EmotePicker
              isOpen={showEmotePicker}
              onClose={() => setShowEmotePicker(false)}
              onEmoteSelect={(emoteCode) => {
                setMessageInput(prev => prev + emoteCode);
                setShowEmotePicker(false);
              }}
            />
            
            <button
              onClick={sendWhisper}
              disabled={!messageInput.trim() || sending}
              className="bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-white p-2 rounded-lg transition-all duration-200"
            >
              <Send size={20} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WhisperModal;
